<template>
  <AppLayout title="Warenkorb">
    <!-- Loading Overlay for Order Placement -->
    <div v-if="isPlacingOrder" class="fixed inset-0 bg-gray-900 bg-opacity-75 z-50 flex items-center justify-center">
      <div class="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
        <svg class="animate-spin h-16 w-16 mx-auto text-sonnenglas-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ $t('Bestellung wird aufgegeben...') }}</h3>
        <p class="text-sm text-gray-600">{{ $t('Bitte warten Sie, während Ihre Bestellung verarbeitet wird.') }}</p>
      </div>
    </div>

    <form id="orderForm" action="/cart" method="POST" class="min-h-screen bg-gray-50">
      <input type="hidden" name="_token" :value="csrfToken">
      <input type="hidden" name="purchase" value="yes">
      <!-- Include all quantities as hidden inputs -->
      <input 
        v-for="(quantity, sku) in localQuantities" 
        :key="sku"
        type="hidden" 
        :name="`quantities[${sku}]`" 
        :value="quantity"
      >

      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 class="text-2xl font-bold text-gray-900">{{ $t('Warenkorb') }}</h1>
        </div>
      </div>

      <!-- Tier Progress Cards -->
      <TierProgressCards :tiers="pricingTiers" :currentTierId="currentTierBasedOnValue" :currency="currency" />

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- 2-Column Layout -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Left Column - Products (2/3 width) -->
          <div class="lg:col-span-2">
            <!-- Products Table -->
            <div class="bg-white shadow overflow-hidden sm:rounded-lg">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {{ $t('Produkt') }}
                    </th>
                    <th colspan="3" class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                      <div class="flex items-center justify-center gap-1">
                        {{ allCartProductsFixedPrice ? $t('Einkaufspreis') : $t('Preisstufen') }}
                        <TierInfoTooltip v-if="!allCartProductsFixedPrice" />
                      </div>
                    </th>
                    <th class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {{ $t('Menge') }}
                    </th>
                    <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {{ $t('Summe') }}
                    </th>
                  </tr>
                </thead>

                <!-- Product Categories -->
                <template v-if="categories.length > 0">
                  <CartProductGroup
                    v-for="category in sortedCategories"
                    v-show="category.isParent || !isParentCollapsed(category.parentCategoryId)"
                    :key="category.id"
                    :group-id="category.id"
                    :group-name="category.name"
                    :is-parent="category.isParent"
                    :is-collapsed="category.isParent && collapsedParentCategories.has(category.id)"
                    :child-category-ids="getChildCategoryIds(category.id)"
                    :products="allProducts"
                    :quantities="quantities"
                    :currency="currency"
                    :pricing-tiers="pricingTiers"
                    :product-tier-pricing="productTierPricing"
                    :current-pricing-tier="currentPricingTier"
                    :tax-rate="taxRate"
                    :min-quantity="getMinQuantityForCategory(category.id)"
                    :pos-config="posConfig"
                    @update-quantity="updateQuantity"
                    @show-pricing-modal="showPricingTiersModal = true"
                    @show-fixed-price-modal="showFixedPriceModal = true"
                    @toggle-collapse="toggleParentCategoryCollapse(category.id)" />
                </template>

                <!-- Fallback: Show all products in single group when no categories -->
                <template v-else>
                  <CartProductGroup :group-id="-1" :group-name="$t('Alle Produkte')" :is-parent="false" :is-collapsed="false" :products="allProducts"
                    :quantities="quantities" :currency="currency" :pricing-tiers="pricingTiers"
                    :product-tier-pricing="productTierPricing" :current-pricing-tier="currentPricingTier"
                    :tax-rate="taxRate" :min-quantity="0" :pos-config="posConfig"
                    @update-quantity="updateQuantity" @show-pricing-modal="showPricingTiersModal = true"
                    @show-fixed-price-modal="showFixedPriceModal = true" />
                </template>
              </table>
            </div>
          </div>

          <!-- Right Column - Order Summary (1/3 width) -->
          <div class="lg:col-span-1">
            <StickyOrderSummary :current-tier-name="currentTierName" :next-tier-name="nextTierName"
              :progress-to-next-tier="progressToNextTier" :total-items="totalQuantity" :product-total="totalNetPrice"
              :net-total="totalNetPrice" :gross-total="totalGrossPrice" :currency="currency"
              :products="allProducts" :quantities="quantities" :product-prices="productPrices"
              :box-quantity="boxQuantity" :shipping-cost="shippingCosts" :pallet-shipping="palletShipping"
              :is-disabled="isOrderButtonDisabled"
              :disabled-reason="orderButtonDisabledReason"
              @place-order="scrollToOrderReview()" />
          </div>
        </div>

        <!-- Order Info Card (start of the review area the sidebar button scrolls to, feedback #02) -->
        <div
          id="order-review-section"
          class="bg-white shadow sm:rounded-lg mt-4 transition-all duration-500"
          :class="{ 'ring-2 ring-sonnenglas-500 ring-offset-2': highlightReview }"
        >
          <div class="px-4 py-5 sm:p-6">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="md:col-span-1">
                <p><span class="font-medium">{{ $t('Zahlungsart') }}:</span> {{ showPaymentMethod }}</p>
              </div>
              <div class="md:col-span-1 text-right">
                <span class="font-medium">{{ $t('Voraussichtliche Lieferzeit') }}</span>
              </div>
              <div v-if="totalQuantity > 0" class="md:col-span-1 text-right" v-html="orderDeliveryTime"></div>
            </div>

            <!-- Order Statistics -->
            <div class="border-t border-gray-200 mt-4 pt-4 space-y-2">
              <div class="flex justify-between text-sm">
                <span>{{ $t('Gesamtanzahl') }}</span>
                <span>{{ totalQuantity }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span>{{ $t('Anzahl Versandkartons') }}</span>
                <span>{{ boxQuantity }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span>{{ $t('Volumen') }}</span>
                <span>{{ formatNumber(shippingVolume) }} cbm</span>
              </div>
              <div class="flex justify-between text-sm">
                <span>{{ $t('Bruttogewicht') }}</span>
                <span>{{ totalWeight }} kg</span>
              </div>
            </div>

            <!-- Products in Order -->
            <div v-if="orderedProductsList.length > 0" class="border-t border-gray-200 mt-4 pt-4">
              <h3 class="text-sm font-semibold text-gray-900 mb-3">{{ $t('Produkte in der Bestellung') }}</h3>
              <div class="space-y-2">
                <div
                  v-for="product in orderedProductsList"
                  :key="product.sku"
                  class="flex justify-between items-center text-sm py-1"
                >
                  <div class="flex-1">
                    <p class="text-gray-900">{{ product.name }} ({{ product.sku }})</p>
                    <!-- Partial pre-order: part ships immediately, the rest is back-ordered. Shown
                         first so a split order is not mislabelled as fully "Sofort verfügbar". -->
                    <p v-if="product.split.isSplit" class="text-xs mt-0.5 font-medium text-amber-700">
                      {{ $t(':available sofort lieferbar, :rest als Vorbestellung', { available: product.split.available, rest: product.split.rest }) }}
                    </p>
                    <p v-else-if="product.deliveryInWeeksFrom > 0 || product.deliveryInWeeksTo > 0"
                      :class="[
                        'text-xs mt-0.5',
                        Math.max(product.deliveryInWeeksFrom, product.deliveryInWeeksTo) > 3 ? 'text-red-600 font-medium' : 'text-yellow-600'
                      ]">
                      {{ $t('Lieferung') }}
                      {{ product.deliveryInWeeksTo >= 8
                        ? `ca ${Math.floor(product.deliveryInWeeksTo / 4)} ${Math.floor(product.deliveryInWeeksTo / 4) > 1 ? $t('Monate') : $t('Monat')}`
                        : `${product.deliveryInWeeksTo} ${product.deliveryInWeeksTo > 1 ? $t('Wochen') : $t('Woche')}`
                      }}
                    </p>
                    <p v-else class="text-xs mt-0.5 text-green-600">{{ $t('Sofort verfügbar') }}</p>
                  </div>
                  <div class="text-right ml-4">
                    <p class="font-medium text-gray-900">{{ product.quantity }} {{ $t('Stk.') }}</p>
                    <p class="text-sm text-gray-600">{{ formatPrice(product.lineTotal) }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Price Breakdown -->
            <div class="border-t border-gray-200 mt-4 pt-4 space-y-2">
              <div class="flex justify-between text-sm">
                <span>{{ $t('Summe') }} {{ $t('UVP') }} ({{ $t('brutto') }})</span>
                <span>{{ formatPrice(getTotalMSRP) }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span>{{ $t('Summe') }} {{ $t('EK') }} ({{ $t('netto') }})</span>
                <span>{{ formatPrice(getTotalNetWithoutShipping) }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span>{{ $t('Versandkosten') }} ({{ $t('netto') }})</span>
                <span>
                  <span v-if="palletShipping || shippingCosts === 0">{{ $t('On Request') }}</span>
                  <span v-else>{{ formatPrice(shippingCosts) }}</span>
                </span>
              </div>
              <div class="flex justify-between text-sm">
                <span>{{ $t('Summe') }} ({{ $t('netto') }})</span>
                <span>{{ formatPrice(totalNetPrice) }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span>{{ $t('zzgl. Umsatzsteuer') }} ({{ formatNumber(taxRate) }}%)</span>
                <span>{{ formatPrice(getTax(totalNetPrice)) }}</span>
              </div>
              <div class="flex justify-between text-base font-semibold border-t border-gray-200 pt-2">
                <span>{{ $t('Summe') }} ({{ $t('brutto') }})</span>
                <span class="underline">
                  {{ formatPrice(totalGrossPrice) }}
                  <span v-if="palletShipping">+ {{ $t('Versandkosten') }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Delivery Settings -->
        <div class="bg-white shadow sm:rounded-lg mt-4">
          <div class="px-4 py-5 sm:p-6">
            <h2 class="text-base font-medium text-gray-900 mb-6">{{ $t('Liefereinstellungen & Terminierung') }}</h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 class="text-sm font-medium text-gray-900 mb-3">{{ $t('Teillieferung') }}</h3>
                <div class="flex items-center">
                  <input v-model="partialDelivery"
                    class="h-4 w-4 text-sonnenglas-600 focus:ring-sonnenglas-500 border-gray-300 rounded"
                    type="checkbox" name="partial_shipping_allowed" value="1">
                  <label class="ml-3 text-sm text-gray-700">
                    {{ $t('Ich bin mit Teillieferungen einverstanden und bekomme so verfügbare Ware ggf. schneller.') }}
                  </label>
                </div>
              </div>
              <div>
                <h3 class="text-sm font-medium text-gray-900 mb-3">{{ $t('Terminierung') }}</h3>
                <div class="space-y-3">
                  <div class="flex items-center">
                    <input v-model="asap"
                      class="h-4 w-4 text-sonnenglas-600 focus:ring-sonnenglas-500 border-gray-300 rounded"
                      type="checkbox" name="asap" value="1">
                    <label class="ml-3 text-sm text-gray-700">
                      {{ $t('schnellstmöglich') }}
                    </label>
                  </div>
                  <div>
                    <label class="block text-sm text-gray-700 mb-1">{{ $t('Angestrebter Liefertermin') }}</label>
                    <input v-model="earliestShippingDate" :disabled="asap === true" :min="minDeliveryDate" type="date"
                      name="earliest_shipping_date"
                      class="w-full border-gray-300 rounded-md focus:border-sonnenglas-500 focus:ring-sonnenglas-500 disabled:bg-gray-100">
                  </div>
                </div>
              </div>
              <div v-if="allowedPalletShipping">
                <h3 class="text-sm font-medium text-gray-900 mb-3">{{ $t('Palettenversand') }}</h3>
                <div class="flex items-center">
                  <input v-model="palletShipping"
                    class="h-4 w-4 text-sonnenglas-600 focus:ring-sonnenglas-500 border-gray-300 rounded"
                    type="checkbox" name="pallet_shipping" value="1">
                  <label class="ml-3 text-sm text-gray-700">
                    {{ $t('Ich bevorzuge Palettenversand') }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Invoice Notes -->
        <div class="bg-white shadow sm:rounded-lg mt-4">
          <div class="px-4 py-5 sm:p-6">
            <h2 class="text-base font-medium text-gray-900 mb-4">
              {{ $t('Gewünschte Rechnungsnotizen (z.B. deine firmeninterne Bestellnummer etc.) bitte hier eingeben.') }}
            </h2>
            <textarea class="w-full border-gray-300 rounded-md focus:border-sonnenglas-500 focus:ring-sonnenglas-500"
              name="invoice_text" rows="3"></textarea>
          </div>
        </div>

        <!-- Delivery and Billing Address -->
        <div class="bg-white shadow sm:rounded-lg mt-4">
          <div class="px-4 py-5 sm:p-6">
            <h2 class="text-base font-medium text-gray-900 mb-6">{{ $t('Liefer- und Rechnungsadresse') }}</h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 class="text-sm font-medium text-gray-900 mb-2">{{ $t('Lieferadresse') }}</h3>
                <select class="w-full border-gray-300 rounded-md focus:border-sonnenglas-500 focus:ring-sonnenglas-500"
                  name="shipping_address_id" required>
                  <option v-for="address in shippingAddresses" :key="address.id" :value="address.id">
                    {{ address.address }}
                  </option>
                </select>
                <a href="/partner-account"
                  class="text-sm text-sonnenglas-600 hover:text-sonnenglas-700 mt-1 inline-block">
                  {{ $t('Adresse bearbeiten oder neu anlegen') }}
                </a>
              </div>
              <div>
                <h3 class="text-sm font-medium text-gray-900 mb-2">{{ $t('Rechnungsadresse') }}</h3>
                <select class="w-full border-gray-300 rounded-md focus:border-sonnenglas-500 focus:ring-sonnenglas-500"
                  name="billing_address_id" required>
                  <option v-for="address in billingAddresses" :key="address.id" :value="address.id">
                    {{ address.address }}
                  </option>
                </select>
                <a href="/partner-account"
                  class="text-sm text-sonnenglas-600 hover:text-sonnenglas-700 mt-1 inline-block">
                  {{ $t('Adresse bearbeiten oder neu anlegen') }}
                </a>
              </div>
              <div class="lg:col-span-1">
                <h3 class="text-sm font-medium text-gray-900 mb-2">{{ $t('Rechnungs-E-Mail') }}</h3>
                <p class="text-sm text-gray-900">{{ invoiceEmail }}</p>
                <a href="/invoice-email"
                  class="text-sm text-sonnenglas-600 hover:text-sonnenglas-700 mt-1 inline-block">
                  {{ $t('Adresse bearbeiten oder neu anlegen') }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Button -->
        <div class="mt-6 text-right">
          <button type="button" @click="submitForm(false)"
            :disabled="isOrderButtonDisabled"
            class="btn btn-sonnenglas px-6 py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed inline-flex flex-col items-center">
            <div class="flex items-center">
              <svg v-if="isUpdating" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg"
                fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                </path>
              </svg>
              <span>{{ orderButtonDisabledReason || $t('Bestellung aufgeben') }}</span>
            </div>
          </button>
        </div>
      </div>

      <!-- Pricing Tiers Modal -->
      <PricingTierModal :show="showPricingTiersModal" :tiers="pricingTiers" :current-tier="currentPricingTier"
        :current-quantity="totalQuantity" @close="showPricingTiersModal = false" />

      <!-- Delivery Warning Modal -->
      <DeliveryWarningModal
        :show="showDeliveryWarningModal"
        :products-with-long-delivery="productsWithLongDelivery"
        :allow-partial-delivery="!partialDelivery"
        @confirm="confirmOrderWithLongDelivery"
        @cancel="cancelOrderWithLongDelivery" />

      <!-- Order Validation Modal -->
      <OrderValidationModal
        :show="showValidationModal"
        :missing-info="validationErrors"
        @close="showValidationModal = false" />
    </form>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance, inject, nextTick } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import CartProductGroup from '@/components/CartProductGroup.vue'
import PricingTierModal from '@/components/PricingTierModal.vue'
import TierProgressCards from '@/components/TierProgressCards.vue'
import TierInfoTooltip from '@/components/TierInfoTooltip.vue'
import StickyOrderSummary from '@/components/StickyOrderSummary.vue'
import DeliveryWarningModal from '@/components/DeliveryWarningModal.vue'
import OrderValidationModal from '@/components/OrderValidationModal.vue'
import { useCartStore } from '@/stores/cart'
import { splitQuantityByAvailability } from '@/utils/deliveryAvailability'

interface Product {
  sku: string
  name: string
  categoryId: number
  productUrl?: string
  description: string
  image?: string
  price?: { value: number; currency: string }
  fixedNetPrice?: { value: number; currency: string }
  fixedNetPriceLaunch?: { value: number; currency: string }
  excludedFromDiscounts?: boolean
  deliveryInWeeksFrom?: number
  deliveryInWeeksTo?: number
  availableQuantities: number[]
  availableQuantity?: number | null
  isPos?: boolean
  weight: number
  height: number
  width: number
  length: number
  cartonQuantity?: number
}


interface Address {
  id: number
  address: string
}

interface Category {
  id: number
  name: string
  parent_category_id: number | null
  sort_order: number
  product_count: number
  children?: Category[]
}

interface HiddenProduct {
  sku: string
  name: string
  reason: string
}

interface PosConfig {
  freeMaterialsTier1: number
  freeMaterialsTier2Plus: number
  additionalItemFee: number
  materialGroupId: number
  tosUrl: string
}

interface Props {
  cart: any
  currency: string
  taxRate: number
  paymentMethod: string
  country: string
  invoiceEmail: string
  allProducts: Product[]
  categories: Category[]
  pricingTiers: PartnerPortal.Xhosa.Contracts.DTOs.ResellerPricingTierDto[]
  productTierPricing: Record<string, Record<string, number>>
  quantities: Record<string, number>
  language: string
  billingAddresses: Address[]
  shippingAddresses: Address[]
  hiddenProducts?: HiddenProduct[]
  shippingCosts: number | null
  packagesCount: number
  shippingError: string | null
  estimatedDeliveryDate: string | null
  posConfig: PosConfig
}

const props = defineProps<Props>()

// Inject Ziggy route helper for script setup
const route = inject('route') as any

// Get CSRF token from page props
const page = usePage()
const csrfToken = computed(() => page.props._token)

// Get the global $t function
const instance = getCurrentInstance()
const $t = instance?.appContext.config.globalProperties.$t || ((key: string) => key)

// Reactive state
const partialDelivery = ref(false)
const asap = ref(true)
const earliestShippingDate = ref('')
const palletShipping = ref(false)
const showPricingTiersModal = ref(false)
const showFixedPriceModal = ref(false)
const isUpdating = ref(false)
const showDeliveryWarningModal = ref(false)
const skipDeliveryWarning = ref(false)
const showValidationModal = ref(false)
const isPlacingOrder = ref(false)
const highlightReview = ref(false)
const validationErrors = ref({
  shippingAddress: false,
  billingAddress: false,
  invoiceEmail: false
})

// Track collapsed parent categories (category IDs that are collapsed)
// Initialize with all categories collapsed - will be updated on mount
const collapsedParentCategories = ref<Set<number>>(new Set())

// Local reactive copy of quantities for form submission
const localQuantities = ref({ ...props.quantities })

// Watch for prop changes to update local quantities
watch(() => props.quantities, (newQuantities) => {
  localQuantities.value = { ...newQuantities }
}, { deep: true })

// Computed values

// Cart total used as the BASE for tier determination.
//
// Bug history: A previous version of this computed read getTotalNetWithoutShipping,
// but that computed itself depends on currentPricingTier (via getNetDiscountedPrice),
// which depends on currentTierBasedOnValue. This circular dependency caused tiers to
// never upgrade in markets such as CH (Switzerland): Vue resolved the cycle by using
// the default tier (tier1) on the first pass, the discounted total computed at tier1
// prices fell below the next tier's threshold, and the cart stayed pinned to tier 1.
//
// Mirror the cart store (resources/js/stores/cart.ts) and use tier1 prices as a
// non-circular base: tier1 is the lowest discount tier, so the resulting cart total
// is the highest possible discounted total and matches the semantic of "would this
// purchase qualify at the next tier?" without ever referencing the current tier.
const cartTotalAtBaseTier = computed(() => {
  let total = 0
  for (const product of props.allProducts) {
    const qty = props.quantities[product.sku] || 0
    if (qty <= 0) continue

    const productPrice = getNetPriceAtBaseTier(product)
    const subtotal = productPrice * qty
    if (!isNaN(subtotal)) {
      total += subtotal
    }
  }
  return total
})

const currentTierBasedOnValue = computed(() => {
  // Calculate current tier based on net order value at tier1 (base) prices.
  // Using tier1 prices avoids the circular dependency described on cartTotalAtBaseTier
  // while keeping the "actual purchase amount" semantic (no RRP/gross uplift).
  const cartTotal = cartTotalAtBaseTier.value
  const sortedTiers = [...props.pricingTiers].sort((a, b) => b.revenueThreshold - a.revenueThreshold)

  const tier = sortedTiers.find(tier => cartTotal >= tier.revenueThreshold)
  return tier ? tier.id : (props.pricingTiers[0]?.id || 1)
})

const currentPricingTier = computed(() => {
  // Keep this for backward compatibility with existing logic
  return props.pricingTiers.find(tier => tier.id === currentTierBasedOnValue.value) || props.pricingTiers[0]
})

// Minimum order value (Mindestbestellwert) = the lowest tier's revenue threshold.
// Stufe 1 entry = 160 € for EUR markets, 180 CHF for Switzerland (per reseller_pricing_tiers),
// already passed through with the correct currency, so this is currency-aware automatically.
const minimumOrderValue = computed(() => {
  if (!props.pricingTiers || props.pricingTiers.length === 0) return 0
  return Math.min(...props.pricingTiers.map(tier => tier.revenueThreshold))
})

// True when a non-POS-only cart has items but its net value (at base-tier prices, excluding
// shipping — the same basis used for tier determination) has not yet reached the Mindestbestellwert.
const belowMinimumOrderValue = computed(() => {
  return !isPosOnlyOrder.value &&
         totalQuantity.value > 0 &&
         minimumOrderValue.value > 0 &&
         cartTotalAtBaseTier.value < minimumOrderValue.value
})

// A product is a fixed price when it has no discount tiers (POS / excluded / fixed net price) or its
// tier prices are identical. Used to drop the "Preisstufen" wording when the whole cart is fixed-price.
const isProductFixedPrice = (product: Product): boolean => {
  if (product.isPos || product.excludedFromDiscounts || product.fixedNetPrice || product.fixedNetPriceLaunch) {
    return true
  }
  const tierPricing = props.productTierPricing[product.sku]
  if (!tierPricing) {
    return false
  }
  const prices = [tierPricing.tier1, tierPricing.tier2, tierPricing.tier3]
    .filter((v) => v !== undefined && v !== null && !isNaN(v))
  return prices.length > 1 && prices.every((v) => v === prices[0])
}

const allCartProductsFixedPrice = computed(() => {
  const inCart = props.allProducts.filter((p) => (props.quantities[p.sku] || 0) > 0)
  return inCart.length > 0 && inCart.every(isProductFixedPrice)
})

const currentTierName = computed(() => {
  return currentPricingTier.value?.name || $t('Preisstufe 1')
})

const nextTierName = computed(() => {
  const sortedTiers = [...props.pricingTiers].sort((a, b) => a.revenueThreshold - b.revenueThreshold)
  const currentIndex = sortedTiers.findIndex(tier => tier.id === currentTierBasedOnValue.value)
  const nextTier = sortedTiers[currentIndex + 1]
  return nextTier?.name
})

const progressToNextTier = computed(() => {
  // Use the same non-circular base (tier1 prices) that drives currentTierBasedOnValue,
  // otherwise the "progress towards next tier" indicator would disagree with the active
  // tier highlight whenever the cart sits near a threshold boundary.
  const cartTotal = cartTotalAtBaseTier.value
  const sortedTiers = [...props.pricingTiers].sort((a, b) => a.revenueThreshold - b.revenueThreshold)
  const currentIndex = sortedTiers.findIndex(tier => tier.id === currentTierBasedOnValue.value)
  const nextTier = sortedTiers[currentIndex + 1]

  if (!nextTier) {
    return undefined // Already at highest tier
  }

  const currentValue = isNaN(cartTotal) ? 0 : cartTotal
  const targetValue = nextTier.revenueThreshold
  const remainingAmount = Math.max(0, targetValue - currentValue)
  const percentage = targetValue > 0 ? Math.min(100, (currentValue / targetValue) * 100) : 0

  return {
    currentValue,
    targetValue,
    remainingAmount,
    percentage
  }
})

const totalQuantity = computed(() => {
  return Object.values(props.quantities).reduce((sum, qty) => sum + qty, 0)
})

const showPaymentMethod = computed(() => {
  return props.paymentMethod === 'invoice' ? ($t('Invoice') as string) : ($t('Vorkasse') as string)
})

const orderDeliveryTime = computed(() => {
  // Use estimated delivery date from Xhosa API if available
  if (props.estimatedDeliveryDate) {
    // Format the date nicely for display
    try {
      const date = new Date(props.estimatedDeliveryDate)
      // Format as localized date string
      return date.toLocaleDateString(document.documentElement.lang || 'de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch (e) {
      console.error('Failed to format delivery date:', e)
      return props.estimatedDeliveryDate
    }
  }

  // Fallback: Calculate aggregate delivery time from product data
  let maxFrom = 0
  let maxTo = 0

  for (const product of props.allProducts) {
    const qty = props.quantities[product.sku] || 0
    if (qty > 0) {
      maxFrom = Math.max(maxFrom, product.deliveryInWeeksFrom || 0)
      maxTo = Math.max(maxTo, product.deliveryInWeeksTo || 0)
    }
  }

  if (maxFrom === 0 && maxTo === 0) return $t('sofort lieferbar') as string
  if (maxFrom === maxTo) {
    return maxFrom === 1 ? `${maxFrom} ${$t('Woche')}` : `${maxFrom} ${$t('Wochen')}`
  }
  return `${maxFrom}-${maxTo} ${$t('Wochen')}`
})

const boxQuantity = computed(() => {
  // Use real packages count from backend (calculated via Xhosa API)
  return props.packagesCount || 0
})

const shippingVolume = computed(() => {
  let volume = 0
  for (const product of props.allProducts) {
    const qty = props.quantities[product.sku] || 0
    if (qty > 0) {
      volume += (product.height * product.width * product.length * qty) / 1000000 // Convert to cubic meters
    }
  }
  return volume
})

const totalWeight = computed(() => {
  let weight = 0
  for (const product of props.allProducts) {
    const qty = props.quantities[product.sku] || 0
    if (qty > 0) {
      weight += (product.weight * qty) / 1000 // Convert to kg
    }
  }
  return Math.round(weight * 100) / 100
})

const getTotalMSRP = computed(() => {
  // Calculate total RRP (MSRP) for display purposes only
  // Note: Tier calculations now use actual discounted prices (getTotalNetWithoutShipping)
  let total = 0
  for (const product of props.allProducts) {
    const qty = props.quantities[product.sku] || 0
    if (qty > 0) {
      const netAmount = (product.price?.value || 0) * qty
      const grossAmount = netAmount * (1 + props.taxRate / 100)
      total += grossAmount
    }
  }
  return total
})

const getTotalNetWithoutShipping = computed(() => {
  let total = 0
  for (const product of props.allProducts) {
    const qty = props.quantities[product.sku] || 0
    if (qty > 0) {
      const productPrice = getNetDiscountedPrice(product)
      const subtotal = productPrice * qty
      if (!isNaN(subtotal)) {
        total += subtotal
      }
    }
  }
  return total
})

const shippingCosts = computed(() => {
  // Use real shipping costs from backend (calculated via Xhosa API)
  // Returns null if shipping calculation failed (no fallback)
  return props.shippingCosts ?? 0
})

const hasShippingError = computed(() => {
  return props.shippingError !== null && props.shippingError !== undefined
})

const totalNetPrice = computed(() => {
  // POS materials are free (price 0) and capped at 1, so the legacy "free POS tier" discount
  // (miscDiscount) was always 0 and has been removed.
  const total = getTotalNetWithoutShipping.value + shippingCosts.value
  return isNaN(total) ? 0 : total
})

const totalGrossPrice = computed(() => {
  const total = totalNetPrice.value * (1 + props.taxRate / 100)
  return isNaN(total) ? 0 : total
})

const currentRealDiscount = computed(() => {
  if (getTotalMSRP.value === 0) return 0
  return Math.round((1 - getTotalNetWithoutShipping.value / getTotalMSRP.value) * 100)
})

const isPosOnlyOrder = computed(() => {
  // Check if all ordered items are POS materials
  return Object.keys(props.quantities).every(sku => {
    const qty = props.quantities[sku]
    if (qty === 0) return true
    const product = props.allProducts.find(p => p.sku === sku)
    return product?.isPos === true
  })
})

const reachedMinimumForProductGroup = computed(() => {
  // Check minimum quantities for specific categories
  // Light Carafes (id=3) and SOMO Adventure Mounts (id=4) require minimum 4
  const categoriesWithMinimum = [
    { id: 3, minQuantity: 4 },
    { id: 4, minQuantity: 4 }
  ]
  
  for (const catReq of categoriesWithMinimum) {
    const groupProducts = props.allProducts.filter(p => p.categoryId === catReq.id)
    const groupTotal = groupProducts.reduce((sum, product) => {
      return sum + (props.quantities[product.sku] || 0)
    }, 0)

    if (groupTotal > 0 && groupTotal < catReq.minQuantity) {
      return false
    }
  }

  return true
})

const hasZeroPricedProducts = computed(() => {
  // Safety check: Disable order button if any products have zero pricing.
  // POS materials are intentionally free, so they are exempt from this check (feedback #07a).
  for (const product of props.allProducts) {
    if (product.isPos) continue
    const qty = props.quantities[product.sku] || 0
    if (qty > 0) {
      const productPrice = getNetDiscountedPrice(product)
      if (productPrice <= 0) {
        console.warn(`Product ${product.sku} has zero or invalid price: ${productPrice}`)
        return true
      }
    }
  }
  return false
})

const hasNoAddresses = computed(() => {
  return props.shippingAddresses.length === 0 || props.billingAddresses.length === 0
})

const isOrderButtonDisabled = computed(() => {
  // Shared logic for both order buttons (sidebar and bottom)
  return isUpdating.value ||
         (!isPosOnlyOrder.value && !reachedMinimumForProductGroup.value) ||
         belowMinimumOrderValue.value ||
         hasZeroPricedProducts.value ||
         hasShippingError.value ||
         hasNoAddresses.value ||
         totalQuantity.value === 0
})

const orderButtonDisabledReason = computed(() => {
  if (totalQuantity.value === 0) {
    return $t('Füge Artikel zu deinem Warenkorb hinzu, um eine Bestellung aufzugeben')
  }
  if (isUpdating.value) {
    return $t('Updating prices...')
  }
  if (hasShippingError.value) {
    return $t('Shipping calculation error')
  }
  if (hasZeroPricedProducts.value) {
    return $t('Einige Produkte haben ungültige Preise')
  }
  if (hasNoAddresses.value) {
    return $t('Bitte füge zuerst eine Liefer- und Rechnungsadresse hinzu')
  }
  if (belowMinimumOrderValue.value) {
    return `${$t('Mindestbestellwert noch nicht erreicht')} (${formatPrice(minimumOrderValue.value)})`
  }
  if (!isPosOnlyOrder.value && !reachedMinimumForProductGroup.value) {
    return $t('Mindestbestellmenge für einige Produktgruppen nicht erreicht')
  }
  return ''
})

const getChildCategoryIds = (parentId: number): number[] => {
  return sortedCategories.value
    .filter(cat => cat.parentCategoryId === parentId)
    .map(cat => cat.id)
}

const getMinQuantityForCategory = (categoryId: number): number => {
  // Maintain existing minimum quantities for specific categories
  switch (categoryId) {
    case 3: // Light Carafes
      return 4
    case 4: // SOMO Adventure Mounts
      return 4
    default:
      return 0
  }
}

const allowedPalletShipping = computed(() => {
  return totalWeight.value > 50 // Allow pallet shipping for heavy orders
})

const productsWithLongDelivery = computed(() => {
  const products: Array<{
    sku: string
    name: string
    quantity: number
    deliveryInWeeksFrom?: number
    deliveryInWeeksTo?: number
    availableQuantity?: number | null
  }> = []

  for (const product of props.allProducts) {
    const qty = props.quantities[product.sku] || 0
    if (qty > 0) {
      const weeksFrom = product.deliveryInWeeksFrom || 0
      const weeksTo = product.deliveryInWeeksTo || 0

      // Include a line in the warning when either the whole product has a long delivery (> 2 weeks),
      // or only part of the chosen quantity can ship immediately (the rest becomes a pre-order) — the
      // latter can have 0-week delivery yet still split, so availableQuantity is checked explicitly.
      const splits = splitQuantityByAvailability(qty, product.availableQuantity).isSplit
      if (weeksFrom > 2 || weeksTo > 2 || splits) {
        products.push({
          sku: product.sku,
          name: product.name,
          quantity: qty,
          deliveryInWeeksFrom: product.deliveryInWeeksFrom,
          deliveryInWeeksTo: product.deliveryInWeeksTo,
          availableQuantity: product.availableQuantity
        })
      }
    }
  }

  return products
})

const hasMixedDeliveryTimes = computed(() => {
  const deliveryTimes = new Set<string>()
  for (const product of props.allProducts) {
    const qty = props.quantities[product.sku] || 0
    if (qty > 0) {
      const weeksFrom = product.deliveryInWeeksFrom || 0
      const weeksTo = product.deliveryInWeeksTo || 0
      deliveryTimes.add(`${weeksFrom}-${weeksTo}`)
    }
  }
  return deliveryTimes.size > 1
})

watch(hasMixedDeliveryTimes, (hasMixed) => {
  if (hasMixed) {
    partialDelivery.value = true
  }
}, { immediate: true })

const minDeliveryDate = computed(() => {
  let maxWeeks = 0
  for (const product of props.allProducts) {
    const qty = props.quantities[product.sku] || 0
    if (qty > 0) {
      const weeksTo = product.deliveryInWeeksTo || 0
      if (weeksTo > maxWeeks) {
        maxWeeks = weeksTo
      }
    }
  }
  const date = new Date()
  date.setDate(date.getDate() + maxWeeks * 7)
  return date.toISOString().split('T')[0]
})


// Flatten categories to include subcategories with localized names
const sortedCategories = computed(() => {
  const currentLocale = (document.documentElement.lang || 'de').toLowerCase()

  const getLocalizedName = (category: Category) => {
    const nameObj = category.name
    if (typeof nameObj === 'object') {
      return currentLocale === 'en' ? (nameObj.en || nameObj.de) : (nameObj.de || nameObj.en)
    }
    return nameObj
  }

  // Helper to check if a category has any products available (not just in cart)
  const hasProductsInCategory = (categoryId: number): boolean => {
    return props.allProducts.some(product => product.categoryId === categoryId)
  }

  // Helper to check if a category tree (parent + children) has any products
  const hasProductsInCategoryTree = (category: Category): boolean => {
    // Check if this category has products
    if (hasProductsInCategory(category.id)) {
      return true
    }

    // Check if any children have products
    if (category.children && category.children.length > 0) {
      return category.children.some(child => hasProductsInCategory(child.id))
    }

    return false
  }

  const flattened: Array<{id: number, name: string, isParent: boolean, parentCategoryId: number | null}> = []

  // Sort top-level categories by sort_order
  const sorted = [...props.categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

  for (const category of sorted) {
    // Only process categories that have products (in the category or its children)
    if (!hasProductsInCategoryTree(category)) {
      continue
    }

    // Always add main category header (even if it has no products, but children do)
    flattened.push({
      id: category.id,
      name: getLocalizedName(category),
      isParent: !category.parent_category_id,
      parentCategoryId: null
    })

    // Add subcategories if they exist and have products
    if (category.children && category.children.length > 0) {
      const sortedChildren = [...category.children].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      for (const child of sortedChildren) {
        // Only add subcategories that have products
        if (hasProductsInCategory(child.id)) {
          flattened.push({
            id: child.id,
            name: getLocalizedName(child),
            isParent: false,
            parentCategoryId: category.id
          })
        }
      }
    }
  }

  return flattened
})

const posProducts = computed(() => {
  return props.allProducts.filter(p => p.isPos === true)
})

const productPrices = computed(() => {
  // Create a map of SKU -> discounted net price for quick lookup
  const priceMap: Record<string, number> = {}
  for (const product of props.allProducts) {
    priceMap[product.sku] = getNetDiscountedPrice(product)
  }
  return priceMap
})

const orderedProductsList = computed(() => {
  return props.allProducts
    .filter(product => {
      const qty = props.quantities[product.sku] || 0
      return qty > 0
    })
    .map(product => {
      const quantity = props.quantities[product.sku] || 0
      const unitPrice = getNetDiscountedPrice(product)
      const lineTotal = unitPrice * quantity

      // Split the chosen quantity into the part that ships immediately and the part that becomes a
      // pre-order, so the order overview reflects a partial pre-order instead of showing fully green.
      // POS is never stock-split (max-1 rule), so pass null availability for it.
      const split = splitQuantityByAvailability(quantity, product.isPos ? null : product.availableQuantity)

      return {
        sku: product.sku,
        name: product.name,
        quantity,
        unitPrice,
        lineTotal,
        deliveryInWeeksFrom: product.deliveryInWeeksFrom || 0,
        deliveryInWeeksTo: product.deliveryInWeeksTo || 0,
        availableQuantity: product.availableQuantity,
        soldOut: product.soldOut,
        isPos: product.isPos,
        split
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
})

// Methods
const formatPrice = (amount: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: props.currency || 'EUR'
  }).format(amount)
}

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num)
}

const getNetDiscountedPrice = (product: Product): number => {
  if (product.excludedFromDiscounts) {
    if (product.fixedNetPrice) {
      return product.fixedNetPrice.value
    }
    return product.price?.value || 0
  }

  if (product.fixedNetPriceLaunch) {
    return product.fixedNetPriceLaunch.value
  }

  if (product.fixedNetPrice) {
    return product.fixedNetPrice.value
  }

  // Use tier-based pricing from productTierPricing
  const productPricing = props.productTierPricing[product.sku]
  if (productPricing) {
    const tierKey = `tier${currentPricingTier.value.id}`
    const tierPrice = productPricing[tierKey]
    if (tierPrice !== undefined && tierPrice !== null && !isNaN(tierPrice)) {
      return tierPrice
    }
  }

  // Fallback to original price
  return product.price?.value || 0
}

// Variant of getNetDiscountedPrice that always uses tier1 prices.
// Required by cartTotalAtBaseTier to break the circular dependency between
// currentPricingTier and the cart total used for tier determination.
const getNetPriceAtBaseTier = (product: Product): number => {
  if (product.excludedFromDiscounts) {
    if (product.fixedNetPrice) {
      return product.fixedNetPrice.value
    }
    return product.price?.value || 0
  }

  if (product.fixedNetPriceLaunch) {
    return product.fixedNetPriceLaunch.value
  }

  if (product.fixedNetPrice) {
    return product.fixedNetPrice.value
  }

  const productPricing = props.productTierPricing[product.sku]
  const tier1Price = productPricing?.tier1
  if (tier1Price !== undefined && tier1Price !== null && !isNaN(tier1Price)) {
    return tier1Price
  }

  return product.price?.value || 0
}

const getTax = (netAmount: number): number => {
  return netAmount * (props.taxRate / 100)
}

// Toggle parent category collapse state
const toggleParentCategoryCollapse = (categoryId: number) => {
  const newSet = new Set(collapsedParentCategories.value)
  if (newSet.has(categoryId)) {
    newSet.delete(categoryId)
  } else {
    newSet.add(categoryId)
  }
  collapsedParentCategories.value = newSet
}

// Check if a parent category is collapsed
const isParentCollapsed = (parentCategoryId: number | null): boolean => {
  if (parentCategoryId === null) return false
  return collapsedParentCategories.value.has(parentCategoryId)
}

const updateQuantity = (sku: string, quantity: number) => {
  // Update local quantities immediately for form submission
  localQuantities.value[sku] = quantity
  
  const quantities = { ...localQuantities.value }
  
  router.post(route('cart'), {
    quantities
  }, {
    preserveState: true,
    preserveScroll: true
  })
}

const validateOrderRequirements = (): boolean => {
  // Check for missing required information
  const form = document.getElementById('orderForm') as HTMLFormElement
  if (!form) return false

  const shippingAddressSelect = form.querySelector('select[name="shipping_address_id"]') as HTMLSelectElement
  const billingAddressSelect = form.querySelector('select[name="billing_address_id"]') as HTMLSelectElement

  const hasShippingAddress = shippingAddressSelect?.value && shippingAddressSelect.value !== ''
  const hasBillingAddress = billingAddressSelect?.value && billingAddressSelect.value !== ''
  const hasInvoiceEmail = props.invoiceEmail && props.invoiceEmail.trim() !== ''

  // Update validation errors
  validationErrors.value = {
    shippingAddress: !hasShippingAddress,
    billingAddress: !hasBillingAddress,
    invoiceEmail: !hasInvoiceEmail
  }

  // Return true if all required fields are filled
  return hasShippingAddress && hasBillingAddress && hasInvoiceEmail
}

// Triggered by the sidebar button: instead of submitting in one click, scroll the customer
// down to the order review area (shipping costs, delivery settings, address, notes) and
// highlight it, so the details are checked before the order is placed via the button at the
// bottom of the page. Prevents accidental one-click orders to the default address (feedback #02).
const scrollToOrderReview = () => {
  const el = document.getElementById('order-review-section')
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    highlightReview.value = true
    setTimeout(() => { highlightReview.value = false }, 2500)
  }
}

const submitForm = () => {
  // Prevent form submission if cart is updating
  if (isUpdating.value) {
    console.warn('Form submission prevented: cart is updating')
    return
  }

  // Validate required information before proceeding
  if (!validateOrderRequirements()) {
    console.warn('Form submission prevented: missing required information')
    showValidationModal.value = true
    return
  }

  // Check for products with long delivery times (more than 2 weeks)
  if (!skipDeliveryWarning.value && productsWithLongDelivery.value.length > 0) {
    showDeliveryWarningModal.value = true
    return
  }

  // All checks passed — place the order directly. There is no B2B TOS to confirm; the previous
  // confirmation linked to the B2C terms (right of withdrawal etc.), so it was removed (feedback AGB).
  placeOrder()
}

const confirmOrderWithLongDelivery = () => {
  // User confirmed they want to proceed with long delivery times
  showDeliveryWarningModal.value = false
  skipDeliveryWarning.value = true
  submitForm()
}

const cancelOrderWithLongDelivery = () => {
  // User canceled the order submission
  showDeliveryWarningModal.value = false
  skipDeliveryWarning.value = false
}


const placeOrder = async () => {
  // Show loading overlay and wait for it to render
  isPlacingOrder.value = true
  await nextTick()

  // Add small additional delay to ensure overlay is visible
  await new Promise(resolve => setTimeout(resolve, 150))

  const form = document.getElementById('orderForm') as HTMLFormElement
  if (form) {
    // Check CSRF token
    const csrfInput = form.querySelector('input[name="_token"]') as HTMLInputElement
    if (!csrfInput || !csrfInput.value) {
      console.error('CSRF token missing in form!')
      alert('Security token missing. Please refresh the page and try again.')
      isPlacingOrder.value = false
      return
    }

    // Check if we have any non-zero quantities
    const hasQuantities = Object.values(localQuantities.value).some(qty => qty && qty > 0)
    if (!hasQuantities) {
      console.warn('No products with quantities > 0 found')
      alert('Please add products to your cart before placing an order.')
      isPlacingOrder.value = false
      return
    }

    // Validate required address fields
    const shippingAddressSelect = form.querySelector('select[name="shipping_address_id"]') as HTMLSelectElement
    const billingAddressSelect = form.querySelector('select[name="billing_address_id"]') as HTMLSelectElement

    if (!shippingAddressSelect?.value) {
      console.error('No shipping address selected')
      alert('Please select a shipping address.')
      shippingAddressSelect?.focus()
      isPlacingOrder.value = false
      return
    }

    if (!billingAddressSelect?.value) {
      console.error('No billing address selected')
      alert('Please select a billing address.')
      billingAddressSelect?.focus()
      isPlacingOrder.value = false
      return
    }

    try {
      form.submit()
    } catch (error) {
      console.error('Error submitting form:', error)
      alert('There was an error submitting your order. Please try again.')
      isPlacingOrder.value = false
    }
  } else {
    console.error('Order form not found!')
    alert('Order form not found. Please refresh the page and try again.')
    isPlacingOrder.value = false
  }
}

// Setup router event listeners for tracking loading state
const setupLoadingTracking = () => {
  router.on('start', (event) => {
    // Track loading state for cart updates (POST to cart route)
    if (event.detail.visit.method === 'post' && event.detail.visit.url.pathname === '/cart') {
      isUpdating.value = true
    }
  })

  router.on('finish', (event) => {
    // Clear loading state when request finishes
    if (event.detail.visit.method === 'post' && event.detail.visit.url.pathname === '/cart') {
      isUpdating.value = false
    }
  })

  router.on('error', (event) => {
    // Clear loading state on error
    if (event.detail.visit.method === 'post' && event.detail.visit.url.pathname === '/cart') {
      isUpdating.value = false
    }
  })
}

// Log hidden products and shipping errors to console on component mount
onMounted(() => {
  setupLoadingTracking()

  // Initialize cart store with backend data so CartSidebar shows correct items
  const cartStore = useCartStore()
  cartStore.initializeCart(
    props.allProducts,
    props.quantities,
    props.pricingTiers,
    props.productTierPricing,
    props.currency
  )

  if (props.hiddenProducts && props.hiddenProducts.length > 0) {
    props.hiddenProducts.forEach(product => {
      console.error(`Product ${product.sku} not displayed: ${product.reason}`)
    })
  }

  if (props.shippingError) {
    console.error('SHIPPING CALCULATION ERROR:', props.shippingError)
    console.error('Order button disabled due to missing shipping costs')
  }

  // Initialize collapsed state: collapse all parent categories that don't have products in cart
  const collapsedSet = new Set<number>()

  props.categories.forEach(category => {
    // Check if this category or any of its children have products in cart
    const hasProductsInCart = (cat: Category): boolean => {
      // Check if category itself has products with quantity > 0
      const hasOwnProducts = props.allProducts.some(product => {
        const qty = props.quantities[product.sku] || 0
        return product.categoryId === cat.id && qty > 0
      })

      if (hasOwnProducts) return true

      // Check if any children have products with quantity > 0
      if (cat.children && cat.children.length > 0) {
        return cat.children.some(child => hasProductsInCart(child))
      }

      return false
    }

    // If this is a parent category (no parent_category_id) and has no products in cart
    if (!category.parent_category_id && !hasProductsInCart(category)) {
      collapsedSet.add(category.id)
    }
  })

  collapsedParentCategories.value = collapsedSet
})

// Clean up event listeners on component unmount
onBeforeUnmount(() => {
  // Note: Inertia doesn't provide a way to remove specific listeners,
  // but they will be cleaned up when the component is destroyed
})
</script>